// ============================================================================
// MODULE DATING - HOOK REALTIME CHAT
// Écoute des messages en temps réel via Supabase Realtime
// ============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { 
  DatingMessage, 
  DatingConversation 
} from '../types/dating';
import {
  getDatingMessages,
  sendDatingMessage,
  markDatingMessagesAsRead,
  getDatingConversations
} from '../services/dating/datingService';

interface UseDatingChatOptions {
  conversationId: string;
  currentProfileId: string;
  onNewMessage?: (message: DatingMessage) => void;
  onTyping?: (isTyping: boolean, profileId: string) => void;
}

interface UseDatingChatReturn {
  messages: DatingMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string, messageType?: 'text' | 'image' | 'gif' | 'audio', mediaUrl?: string) => Promise<boolean>;
  loadMoreMessages: () => Promise<void>;
  hasMoreMessages: boolean;
  markAsRead: () => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  otherUserTyping: boolean;
}

/**
 * Hook pour gérer le chat en temps réel
 */
export const useDatingChat = ({
  conversationId,
  currentProfileId,
  onNewMessage,
  onTyping
}: UseDatingChatOptions): UseDatingChatReturn => {
  const [messages, setMessages] = useState<DatingMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  
  const channelRef = useRef<RealtimeChannel | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesPerPage = 50;

  // Charger plus de messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || messages.length === 0) return;
    
    const oldestMessage = messages[0];
    
    try {
      const olderMessages = await getDatingMessages(
        conversationId, 
        messagesPerPage, 
        oldestMessage.sent_at
      );
      
      if (olderMessages.length < messagesPerPage) {
        setHasMoreMessages(false);
      }
      
      setMessages(prev => [...olderMessages, ...prev]);
    } catch (err) {
      console.error('Erreur chargement messages anciens:', err);
    }
  }, [conversationId, hasMoreMessages, messages]);

  // Envoyer un message avec optimistic update
  const sendMessage = useCallback(async (
    content: string,
    messageType: 'text' | 'image' | 'gif' | 'audio' = 'text',
    mediaUrl?: string
  ): Promise<boolean> => {
    if (!content.trim() && !mediaUrl) return false;
    
    // Créer un message temporaire pour l'affichage immédiat (optimistic update)
    const tempId = `temp-${Date.now()}`;
    const tempMessage: DatingMessage = {
      id: tempId,
      conversation_id: conversationId,
      sender_id: currentProfileId,
      tenant_id: 0, // Sera remplacé par le vrai tenant_id après envoi
      content: content.trim(),
      message_type: messageType,
      media_url: mediaUrl || undefined,
      sent_at: new Date().toISOString(),
      read_at: undefined,
      status: 'sent',
      is_deleted: false
    };
    
    // Ajouter le message temporaire immédiatement
    setMessages(prev => [...prev, tempMessage]);
    
    try {
      const result = await sendDatingMessage(
        conversationId,
        currentProfileId,
        content,
        messageType,
        mediaUrl
      );
      
      if (result.success && result.message_id) {
        // Mettre à jour l'ID du message temporaire avec le vrai ID
        setMessages(prev => 
          prev.map(m => m.id === tempId ? { ...m, id: result.message_id! } : m)
        );
      } else if (!result.success) {
        // En cas d'échec, retirer le message temporaire
        setMessages(prev => prev.filter(m => m.id !== tempId));
      }
      
      return result.success;
    } catch (err) {
      console.error('Erreur envoi message:', err);
      // Retirer le message temporaire en cas d'erreur
      setMessages(prev => prev.filter(m => m.id !== tempId));
      return false;
    }
  }, [conversationId, currentProfileId]);

  // Marquer les messages comme lus
  const markAsRead = useCallback(async () => {
    try {
      await markDatingMessagesAsRead(conversationId, currentProfileId);
    } catch (err) {
      console.error('Erreur marquage messages lus:', err);
    }
  }, [conversationId, currentProfileId]);

  // Indiquer que l'utilisateur tape
  const setTyping = useCallback((isTyping: boolean) => {
    if (!channelRef.current) return;
    
    channelRef.current.send({
      type: 'broadcast',
      event: 'typing',
      payload: {
        profile_id: currentProfileId,
        is_typing: isTyping
      }
    });
  }, [currentProfileId]);

  // Charger les messages une seule fois au montage
  const hasLoadedRef = useRef(false);
  
  // Configurer le channel Realtime
  useEffect(() => {
    // Ne rien faire si pas d'IDs valides (vides ou undefined)
    if (!conversationId || conversationId.trim() === '' || 
        !currentProfileId || currentProfileId.trim() === '') return;

    // Charger les messages initiaux une seule fois
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      
      const fetchMessages = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
          const data = await getDatingMessages(conversationId, messagesPerPage);
          setMessages(data);
          setHasMoreMessages(data.length === messagesPerPage);
        } catch (err) {
          console.error('Erreur chargement messages:', err);
          setError('Impossible de charger les messages');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchMessages();
    }

    // Créer le channel Realtime
    const channel = supabase.channel(`dating-chat-${conversationId}`, {
      config: {
        broadcast: { self: false },
        presence: { key: currentProfileId }
      }
    });

    // Écouter les nouveaux messages (via postgres_changes)
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'dating_messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        const newMessage = payload.new as DatingMessage;
        
        // Ajouter le message à la liste
        setMessages(prev => {
          // Éviter les doublons
          if (prev.some(m => m.id === newMessage.id)) {
            return prev;
          }
          return [...prev, newMessage];
        });
        
        // Callback optionnel
        if (onNewMessage) {
          onNewMessage(newMessage);
        }
        
        // Marquer comme lu si ce n'est pas notre message
        if (newMessage.sender_id !== currentProfileId) {
          markDatingMessagesAsRead(conversationId, currentProfileId).catch(console.error);
        }
      }
    );

    // Écouter les mises à jour de messages (statut lu)
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'dating_messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        const updatedMessage = payload.new as DatingMessage;
        
        setMessages(prev => 
          prev.map(m => m.id === updatedMessage.id ? updatedMessage : m)
        );
      }
    );

    // Écouter les événements de frappe (broadcast)
    channel.on('broadcast', { event: 'typing' }, (payload) => {
      const { profile_id, is_typing } = payload.payload;
      
      if (profile_id !== currentProfileId) {
        setOtherUserTyping(is_typing);
        
        if (onTyping) {
          onTyping(is_typing, profile_id);
        }
        
        // Reset après 3 secondes si pas de mise à jour
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        
        if (is_typing) {
          typingTimeoutRef.current = setTimeout(() => {
            setOtherUserTyping(false);
          }, 3000);
        }
      }
    });

    // S'abonner au channel
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`Connecté au chat ${conversationId}`);
      }
    });

    channelRef.current = channel;

    // Cleanup
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [conversationId, currentProfileId]); // Dépendances minimales

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
    markAsRead,
    setTyping,
    otherUserTyping
  };
};

// ============================================================================
// HOOK POUR ÉCOUTER LES NOUVELLES CONVERSATIONS / MATCHES
// ============================================================================

interface UseDatingNotificationsOptions {
  profileId: string;
  onNewMatch?: (matchId: string) => void;
  onNewConversation?: (conversationId: string) => void;
  onNewMessage?: (conversationId: string, message: DatingMessage) => void;
}

interface UseDatingNotificationsReturn {
  conversations: DatingConversation[];
  unreadCount: number;
  isLoading: boolean;
  refreshConversations: () => Promise<void>;
}

/**
 * Hook pour les notifications globales (matches, messages)
 */
export const useDatingNotifications = ({
  profileId,
  onNewMatch,
  onNewConversation,
  onNewMessage
}: UseDatingNotificationsOptions): UseDatingNotificationsReturn => {
  const [conversations, setConversations] = useState<DatingConversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const isLoadingRef = useRef(false); // Éviter les appels multiples

  // Charger les conversations (avec protection contre les appels multiples)
  const refreshConversations = useCallback(async () => {
    // Ne pas exécuter si pas de profileId valide
    if (!profileId) {
      setIsLoading(false);
      return;
    }
    
    // Éviter les appels simultanés
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    try {
      const data = await getDatingConversations(profileId);
      setConversations(data);
    } catch (err) {
      console.error('Erreur chargement conversations:', err);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [profileId]);

  // Calculer le nombre de messages non lus
  const unreadCount = conversations.reduce((total, conv) => {
    if (conv.participant1_id === profileId) {
      return total + (conv.unread_count_p1 || 0);
    }
    return total + (conv.unread_count_p2 || 0);
  }, 0);

  useEffect(() => {
    // Ne rien faire si pas de profileId valide (vide ou undefined)
    if (!profileId || profileId.trim() === '') {
      setIsLoading(false);
      return;
    }

    // Charger les conversations initiales (une seule fois)
    refreshConversations();

    // Créer le channel pour les notifications Realtime
    const channel = supabase.channel(`dating-notifications-${profileId}`);

    // Écouter les nouveaux matches
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'dating_matches',
        filter: `profile1_id=eq.${profileId}`
      },
      (payload) => {
        if (onNewMatch) {
          onNewMatch(payload.new.id);
        }
        refreshConversations();
      }
    );

    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'dating_matches',
        filter: `profile2_id=eq.${profileId}`
      },
      (payload) => {
        if (onNewMatch) {
          onNewMatch(payload.new.id);
        }
        refreshConversations();
      }
    );

    // Écouter les nouvelles conversations
    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'dating_conversations',
        filter: `participant1_id=eq.${profileId}`
      },
      (payload) => {
        if (onNewConversation) {
          onNewConversation(payload.new.id);
        }
        refreshConversations();
      }
    );

    channel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'dating_conversations',
        filter: `participant2_id=eq.${profileId}`
      },
      (payload) => {
        if (onNewConversation) {
          onNewConversation(payload.new.id);
        }
        refreshConversations();
      }
    );

    // Écouter les mises à jour de conversations (nouveaux messages)
    channel.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'dating_conversations'
      },
      (payload) => {
        const updated = payload.new as DatingConversation;
        
        // Vérifier si c'est une de nos conversations
        if (updated.participant1_id === profileId || updated.participant2_id === profileId) {
          setConversations(prev => {
            // Vérifier si la conversation existe déjà
            const exists = prev.some(c => c.id === updated.id);
            
            if (exists) {
              // Mettre à jour la conversation existante
              return prev.map(c => c.id === updated.id ? { ...c, ...updated } : c)
                .sort((a, b) => {
                  const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                  const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                  return dateB - dateA;
                });
            } else {
              // Ajouter la nouvelle conversation
              return [...prev, updated].sort((a, b) => {
                const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
                const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
                return dateB - dateA;
              });
            }
          });
        }
      }
    );

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [profileId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    conversations,
    unreadCount,
    isLoading,
    refreshConversations
  };
};

// ============================================================================
// HOOK POUR LE STATUT EN LIGNE
// ============================================================================

interface UseOnlineStatusOptions {
  profileId: string;
  updateInterval?: number; // en ms, défaut 30s
}

/**
 * Hook pour gérer le statut en ligne
 */
export const useDatingOnlineStatus = ({
  profileId,
  updateInterval = 60000 // Augmenté à 60 secondes pour réduire les requêtes
}: UseOnlineStatusOptions): void => {
  useEffect(() => {
    // Ne rien faire si pas de profileId valide (vide ou undefined)
    if (!profileId || profileId.trim() === '') return;

    // Marquer comme en ligne au montage
    const setOnline = async () => {
      await supabase
        .from('dating_profiles')
        .update({ 
          is_online: true, 
          last_active_at: new Date().toISOString() 
        })
        .eq('id', profileId);
    };

    // Marquer comme hors ligne
    const setOffline = async () => {
      await supabase
        .from('dating_profiles')
        .update({ is_online: false })
        .eq('id', profileId);
    };

    // Mettre à jour périodiquement
    setOnline();
    const interval = setInterval(setOnline, updateInterval);

    // Gérer la fermeture de la page
    const handleBeforeUnload = () => {
      // Utiliser sendBeacon pour garantir l'envoi
      navigator.sendBeacon(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/dating_profiles?id=eq.${profileId}`,
        JSON.stringify({ is_online: false })
      );
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setOffline();
      } else {
        setOnline();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setOffline();
    };
  }, [profileId, updateInterval]);
};
